import { formComponents as fc } from "@/features/i18n/formComponents";
import { createFileRoute } from "@tanstack/react-router";
import { useAuth } from "@/utils/auth";
import { hooks as h } from "@/hooks";
import { ui } from "@patient-watch/ui";
import { AlertDialogCancel } from "@patient-watch/ui/alert-dialog";
import { Badge } from "@patient-watch/ui/badge";
import { Button } from "@patient-watch/ui/button";
import { Switch } from "@patient-watch/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import {
    type ColumnDef,
    type SortingState,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Copy, Loader2, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import UnauthorizedPage from "@/components/pages/UnauthorizedPage";
import {
    injectionLocationsBuilderQueryOptions,
    useInjectionLocationsBuilderQuery,
} from "@/features/injection/api/selectInjectionLocationsForBuilder";
import { useUpdateInjectionLocationIsPublic } from "@/features/injection/api/updateInjectionLocationIsPublic";
import { useDeleteInjectionLocation } from "@/features/injection/api/deleteInjectionLocation";
import { useHandleNewInjectionLocation } from "@/features/injection/api/handleNewInjectionLocation";
import InjectionLocationEditForm from "@/features/injection/components/InjectionLocationEditForm";
import type { InjectionLocationRow as EditFormRow } from "@/features/injection/components/InjectionLocationEditForm";
import InjectionLocationForm from "@/features/injection/components/InjectionLocationForm";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";

interface InjectionLocationRow {
    orthopaedic_area: string;
    site: string;
    subsite: string;
    is_public?: boolean;
}

const InjectionLocationsBuilderPage = () => {
    const auth = useAuth();
    const { mutate: updateIsPublic } = useUpdateInjectionLocationIsPublic();
    const { mutate: deleteLocation } = useDeleteInjectionLocation();
    const { mutate: duplicateLocation } = useHandleNewInjectionLocation();
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editingRow, setEditingRow] = useState<InjectionLocationRow | null>(null);
    const [deletingRow, setDeletingRow] = useState<InjectionLocationRow | null>(null);

    h.useNav([
        { label: "Builder", route: { to: "/builder" } },
        { label: "Injection locations", route: { to: "/builder/injection-locations" } },
    ]);

    const { data: locations, isLoading } = useInjectionLocationsBuilderQuery();
    const rows = useMemo(
        () =>
            ([...(locations ?? [])] as InjectionLocationRow[]).sort(
                (a, b) =>
                    a.orthopaedic_area.localeCompare(b.orthopaedic_area) ||
                    a.site.localeCompare(b.site) ||
                    a.subsite.localeCompare(b.subsite)
            ),
        [locations]
    );

    if (!auth?.roles?.isAdmin && !auth?.isSuperUser) {
        return <UnauthorizedPage message="Admin Only Page" />;
    }

    const [sorting, setSorting] = useState<SortingState>([{ id: "orthopaedic_area", desc: false }]);

    const columns: ColumnDef<InjectionLocationRow>[] = useMemo(
        () => [
            {
                accessorKey: "orthopaedic_area",
                header: ({ column }) => (
                    <button
                        type="button"
                        className="flex items-center gap-1 font-medium hover:text-foreground"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Anatomic area
                        {{
                            asc: " ↑",
                            desc: " ↓",
                        }[column.getIsSorted() as string] ?? " ↕"}
                    </button>
                ),
            },
            {
                accessorKey: "site",
                header: ({ column }) => (
                    <button
                        type="button"
                        className="flex items-center gap-1 font-medium hover:text-foreground"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Site
                        {{
                            asc: " ↑",
                            desc: " ↓",
                        }[column.getIsSorted() as string] ?? " ↕"}
                    </button>
                ),
            },
            {
                accessorKey: "subsite",
                header: ({ column }) => (
                    <button
                        type="button"
                        className="flex items-center gap-1 font-medium hover:text-foreground"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Subsite
                        {{
                            asc: " ↑",
                            desc: " ↓",
                        }[column.getIsSorted() as string] ?? " ↕"}
                    </button>
                ),
            },
            {
                accessorKey: "is_public",
                header: "Public",
                cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={row.original.is_public === true}
                            onCheckedChange={(checked) =>
                                updateIsPublic({
                                    key: {
                                        orthopaedic_area: row.original.orthopaedic_area,
                                        site: row.original.site,
                                        subsite: row.original.subsite,
                                    },
                                    is_public: checked === true,
                                })
                            }
                        />
                        <Badge variant={row.original.is_public ? "default" : "secondary"} className="text-xs">
                            {row.original.is_public ? "Yes" : "No"}
                        </Badge>
                    </div>
                ),
            },
            {
                id: "actions",
                header: () => <span className="sr-only">Actions</span>,
                cell: ({ row }) => (
                    <ui.DropdownMenu>
                        <ui.DropdownMenuTrigger asChild>
                            <Button type="button" variant="ghost" size="icon" aria-label="Actions">
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </ui.DropdownMenuTrigger>
                        <ui.DropdownMenuContent align="end">
                            <ui.DropdownMenuItem onClick={() => setEditingRow(row.original)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                            </ui.DropdownMenuItem>
                            <ui.DropdownMenuItem
                                onClick={() =>
                                    duplicateLocation({
                                        user_id: auth.id,
                                        location: {
                                            orthopaedic_area: row.original.orthopaedic_area,
                                            site: row.original.site,
                                            subsite: `${row.original.subsite} (copy)`,
                                        },
                                    })
                                }
                            >
                                <Copy className="mr-2 h-4 w-4" />
                                Duplicate
                            </ui.DropdownMenuItem>
                            <ui.DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setDeletingRow(row.original)}
                            >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                            </ui.DropdownMenuItem>
                        </ui.DropdownMenuContent>
                    </ui.DropdownMenu>
                ),
            },
        ],
        [updateIsPublic, duplicateLocation, auth.id]
    );

    const table = useReactTable({
        data: rows,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getRowId: (row) => `${row.orthopaedic_area}::${row.site}::${row.subsite}`,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-2 md:p-4">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="font-semibold text-3xl tracking-tight">Injection locations</h1>
                    <p className="text-muted-foreground">
                        Manage anatomic area/site/subsite options for injections. Public locations are offered to every
                        clinician; private ones only to the person who created them. Diaries that already use a location
                        keep showing it.
                    </p>
                </div>
                <ui.Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                    <ui.DialogTrigger asChild>
                        <Button size="sm">
                            <Plus className="mr-2 w-4 h-4" />
                            Add injection location
                        </Button>
                    </ui.DialogTrigger>
                    <ui.DialogContent>
                        <ui.DialogHeader>
                            <ui.DialogTitle>Add injection location</ui.DialogTitle>
                            <ui.DialogDescription>Add a new area/site/subsite combination.</ui.DialogDescription>
                        </ui.DialogHeader>
                        <InjectionLocationForm onSuccess={() => setAddDialogOpen(false)} />
                    </ui.DialogContent>
                </ui.Dialog>
            </div>

            <ui.Dialog open={editingRow !== null} onOpenChange={(open) => !open && setEditingRow(null)}>
                <ui.DialogContent>
                    <ui.DialogHeader>
                        <ui.DialogTitle>Edit injection location</ui.DialogTitle>
                        <ui.DialogDescription>Change the anatomic area, site, or subsite.</ui.DialogDescription>
                    </ui.DialogHeader>
                    {editingRow && (
                        <InjectionLocationEditForm
                            initialValues={editingRow as EditFormRow}
                            onSuccess={() => setEditingRow(null)}
                            onCancel={() => setEditingRow(null)}
                        />
                    )}
                </ui.DialogContent>
            </ui.Dialog>

            <ui.AlertDialog open={deletingRow !== null} onOpenChange={(open) => !open && setDeletingRow(null)}>
                <ui.AlertDialogContent>
                    <ui.AlertDialogHeader>
                        <ui.AlertDialogTitle>Delete injection location?</ui.AlertDialogTitle>
                        <ui.AlertDialogDescription>
                            This will permanently delete{" "}
                            <strong>
                                {deletingRow?.orthopaedic_area} / {deletingRow?.site} / {deletingRow?.subsite}
                            </strong>
                            . This action cannot be undone.
                        </ui.AlertDialogDescription>
                    </ui.AlertDialogHeader>
                    <ui.AlertDialogFooter>
                        <AlertDialogCancel asChild>
                            <fc.CancelButton size="default" className="w-auto" />
                        </AlertDialogCancel>
                        <ui.AlertDialogAction asChild>
                            <fc.DeleteButton
                                size="default"
                                onClick={() => {
                                    if (deletingRow) {
                                        deleteLocation({
                                            orthopaedic_area: deletingRow.orthopaedic_area,
                                            site: deletingRow.site,
                                            subsite: deletingRow.subsite,
                                        });
                                    }
                                    setDeletingRow(null);
                                }}
                                className="w-auto"
                            />
                        </ui.AlertDialogAction>
                    </ui.AlertDialogFooter>
                </ui.AlertDialogContent>
            </ui.AlertDialog>

            <ui.Card>
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className={cn(
                                            header.column.getCanSort() &&
                                                "cursor-pointer select-none hover:bg-slate-50 dark:hover:bg-slate-800"
                                        )}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="py-12 text-muted-foreground text-center">
                                    No injection locations found. Add one to get started.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </ui.Card>
        </div>
    );
};

export const Route = createFileRoute("/_app/builder/injection-locations/")({
    component: InjectionLocationsBuilderPage,
    loader: async ({ context }) => {
        await context.queryClient.ensureQueryData(injectionLocationsBuilderQueryOptions());
    },
});
